import { useEffect, useState } from 'react'

import { browserCookies } from '@gousto-internal/storage-consent-web-package'
import { useDecision } from '@optimizely/react-sdk'
import { useDispatch, useSelector } from 'react-redux'
import useSWR from 'swr'

import { menuExperimentsFetcher } from '@library/api-menu-experience'

import userActions from 'actions/user'
import { getIsAuthenticated } from 'selectors/auth'
import { getUserSubscriptionState } from 'selectors/user'

import { GOUSTO_ANYTIME_FLAG } from './useDeliverySettings'
import { GOUSTO_ANYTIME_MENU_EXPERIMENT_KEY, VARIANT } from './useGoustoAnytimeMenuVariant'

type MenuDeeplinkHydration = {
  // Whether a TRX-variant customer should land directly on /menu rather than
  // being bounced to /delivery-details (TM-1571).
  isEligible: boolean
  // Whether the experiment decision (and, for the experiment audience, the
  // customer's subscription state) has settled. The menu must not redirect or
  // hydrate until this is true, otherwise eligible customers get bounced during
  // the async decision load.
  isResolved: boolean
}

export const useMenuDeeplinkHydration = (): MenuDeeplinkHydration => {
  const dispatch = useDispatch()
  const [decision, clientReady, didTimeout] = useDecision(GOUSTO_ANYTIME_FLAG)
  // Treat a timed-out Optimizely decision as resolved (it falls back to the
  // default, disabled decision), so a slow/unavailable SDK degrades to the
  // control /delivery-details bounce instead of leaving the menu unhydrated.
  const decisionResolved = clientReady || didTimeout

  // The experiments response is identity-specific (the fetcher sends the auth and
  // experimentation-user-id headers), so key the SWR cache by that id — otherwise a
  // constant key lets a cached variant/control decision leak across identities. Skip
  // the fetch without an exp id: the endpoint 400s without the header anyway.
  const [, expUserIdCookie] = browserCookies.getJSON<{ exp_user_id: string }>('v1_exp_user_id')
  const experimentationUserId = expUserIdCookie?.exp_user_id
  const shouldFetchExperiment = decision.enabled && Boolean(experimentationUserId)
  const { data, error } = useSWR(
    shouldFetchExperiment ? ['gousto-anytime-menu-experiments', experimentationUserId] : null,
    () => menuExperimentsFetcher(),
  )
  const isAuthenticated = useSelector(getIsAuthenticated)
  const subscriptionState = useSelector(getUserSubscriptionState)
  const [userLoadSettled, setUserLoadSettled] = useState(false)

  const isInExperimentVariant = data?.experiments[GOUSTO_ANYTIME_MENU_EXPERIMENT_KEY] === VARIANT

  // Subscription state isn't loaded on a cold /menu deeplink, but we need it to
  // exclude active subscribers (AC5). Only load it for resolved variant customers
  // — control/non-audience traffic doesn't need it, so it shouldn't incur an extra
  // /user request or wait on it. We settle on both success and failure: a failed
  // /user request must degrade to the normal /delivery-details bounce, never leave
  // the customer on a permanently blank menu.
  useEffect(() => {
    if (!(isInExperimentVariant && isAuthenticated && subscriptionState === undefined)) {
      return undefined
    }

    let cancelled = false
    Promise.resolve(dispatch(userActions.userLoadData()) as unknown as Promise<unknown>)
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setUserLoadSettled(true)
        }
      })

    return () => {
      cancelled = true
    }
  }, [isInExperimentVariant, isAuthenticated, subscriptionState, dispatch])

  // A known, non-active subscription is required: an unknown state (e.g. the
  // /user load failed) is treated as ineligible so the customer bounces rather
  // than hydrating against incomplete data.
  const isEligibleSubscription = subscriptionState === 'inactive'

  // The server experiment is settled once SWR has returned data or errored; when we
  // don't fetch (non-audience, or no exp id) there's nothing to wait on.
  const serverExperimentResolved =
    !shouldFetchExperiment || data !== undefined || error !== undefined
  const variantResolved = decisionResolved && serverExperimentResolved
  // Subscription only matters for resolved variant customers; a resolved control
  // (or non-audience) decision is already subscription-resolved, so a slow /user
  // call can't delay their /delivery-details redirect. Settling on the load attempt
  // (success or failure) for variant users prevents an indefinite hang.
  const subscriptionResolved =
    !isAuthenticated || !isInExperimentVariant || subscriptionState !== undefined || userLoadSettled

  return {
    // Auth is part of eligibility so callers don't have to gate on it separately
    // (the one-off hydration only applies to signed-in customers).
    isEligible: isAuthenticated && isInExperimentVariant && isEligibleSubscription,
    isResolved: variantResolved && subscriptionResolved,
  }
}
